import { useMutation } from "@apollo/client";
import { gql } from "@/gql";
import {
  AdvancedPatternInput,
  BulkShiftUpdates,
  CreateTemplateInput,
  CustomScheduleInput,
  UpdateShiftInput,
  UpdateTemplateInput,
} from "@/gql/graphql";
import { useAsyncAction } from "@/lib/hooks/use-async-action";

// =============================================
// TEMPLATE MUTATIONS (Layer 1)
// =============================================

const CREATE_SHIFT_TEMPLATE = gql(`
  mutation CreateShiftTemplate($franchiseId: ID!, $input: CreateTemplateInput!) {
    createShiftTemplate(franchiseId: $franchiseId, input: $input) {
      id
      name
      description
      position
      scheduleType
      days
      color
      icon
      sortOrder
      isActive
      franchiseId
      createdAt
      updatedAt
      timeIntervals {
        id
        startTime
        endTime
        sortOrder
        templateId
      }
      breakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        templateId
      }
    }
  }
`);

const UPDATE_SHIFT_TEMPLATE = gql(`
  mutation UpdateShiftTemplate($id: ID!, $input: UpdateTemplateInput!) {
    updateShiftTemplate(id: $id, input: $input) {
      id
      name
      description
      position
      scheduleType
      days
      color
      icon
      sortOrder
      isActive
      franchiseId
      createdAt
      updatedAt
      timeIntervals {
        id
        startTime
        endTime
        sortOrder
        templateId
      }
      breakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        templateId
      }
    }
  }
`);

const DELETE_SHIFT_TEMPLATE = gql(`
  mutation DeleteShiftTemplate($id: ID!) {
    deleteShiftTemplate(id: $id)
  }
`);

const DUPLICATE_SHIFT_TEMPLATE = gql(`
  mutation DuplicateShiftTemplate($id: ID!, $newName: String!) {
    duplicateShiftTemplate(id: $id, newName: $newName) {
      id
      name
      description
      position
      scheduleType
      days
      color
      icon
      sortOrder
      isActive
      franchiseId
      createdAt
      updatedAt
      timeIntervals {
        id
        startTime
        endTime
        sortOrder
        templateId
      }
      breakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        templateId
      }
    }
  }
`);

const PROPAGATE_TEMPLATE_CHANGES = gql(`
  mutation PropagateTemplateChanges($templateId: ID!) {
    propagateTemplateChanges(templateId: $templateId)
  }
`);

// =============================================
// PLAN MUTATIONS (Layer 2)
// =============================================

const ASSIGN_TEMPLATE_TO_WORKER = gql(`
  mutation AssignTemplateToWorker($templateId: ID!, $workerSalonId: ID!) {
    assignTemplateToWorker(templateId: $templateId, workerSalonId: $workerSalonId) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      template {
        id
        name
        description
        color
        icon
      }
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

const CREATE_CUSTOM_WORKER_PLAN = gql(`
  mutation CreateCustomWorkerPlan($workerSalonId: ID!, $scheduleData: CustomScheduleInput!) {
    createCustomWorkerPlan(workerSalonId: $workerSalonId, scheduleData: $scheduleData) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

const UPDATE_CUSTOM_WORKER_PLAN = gql(`
  mutation UpdateCustomWorkerPlan($planId: ID!, $scheduleData: CustomScheduleInput!) {
    updateCustomWorkerPlan(planId: $planId, scheduleData: $scheduleData) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

const CONVERT_TEMPLATE_TO_CUSTOM_PLAN = gql(`
  mutation ConvertTemplateToCustomPlan($planId: ID!) {
    convertTemplateToCustomPlan(planId: $planId) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

const DELETE_WORKER_PLAN = gql(`
  mutation DeleteWorkerPlan($planId: ID!) {
    deleteWorkerPlan(planId: $planId)
  }
`);

const REPLACE_WORKER_PLAN = gql(`
  mutation ReplaceWorkerPlan($workerSalonId: ID!, $templateId: ID!) {
    replaceWorkerPlan(workerSalonId: $workerSalonId, templateId: $templateId) {
      id
      workerSalonId
      planType
      templateId
      customScheduleType
      customDays
      isActive
      createdAt
      updatedAt
      template {
        id
        name
        description
        color
        icon
      }
      customTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        planId
      }
      customBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        planId
      }
    }
  }
`);

// =============================================
// PATTERN MUTATIONS (Layer 3)
// =============================================

const GENERATE_PATTERN_FROM_TEMPLATE = gql(`
  mutation GeneratePatternFromTemplate($planId: ID!, $templateId: ID!) {
    generatePatternFromTemplate(planId: $planId, templateId: $templateId) {
      id
      planId
      franchiseId
      sourceType
      managedBy
      resolvedDays
      resolvedScheduleType
      rotationData
      seasonalRules
      exceptionDates
      patternType
      startDate
      endDate
      nextGeneration
      lastGenerated
      isActive
      generateAhead
      createdAt
      updatedAt
      resolvedTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        patternId
      }
      resolvedBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        patternId
      }
    }
  }
`);

const GENERATE_PATTERN_FROM_CUSTOM_PLAN = gql(`
  mutation GeneratePatternFromCustomPlan($planId: ID!) {
    generatePatternFromCustomPlan(planId: $planId) {
      id
      planId
      franchiseId
      sourceType
      managedBy
      resolvedDays
      resolvedScheduleType
      rotationData
      seasonalRules
      exceptionDates
      patternType
      startDate
      endDate
      nextGeneration
      lastGenerated
      isActive
      generateAhead
      createdAt
      updatedAt
      resolvedTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        patternId
      }
      resolvedBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        patternId
      }
    }
  }
`);

const CREATE_DIRECT_PATTERN = gql(`
  mutation CreateDirectPattern($franchiseId: ID!, $workerSalonId: ID!, $patternData: AdvancedPatternInput!) {
    createDirectPattern(franchiseId: $franchiseId, workerSalonId: $workerSalonId, patternData: $patternData) {
      id
      planId
      franchiseId
      sourceType
      managedBy
      resolvedDays
      resolvedScheduleType
      rotationData
      seasonalRules
      exceptionDates
      patternType
      startDate
      endDate
      nextGeneration
      lastGenerated
      isActive
      generateAhead
      createdAt
      updatedAt
      resolvedTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        patternId
      }
      resolvedBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        patternId
      }
    }
  }
`);

const UPDATE_DIRECT_PATTERN = gql(`
  mutation UpdateDirectPattern($patternId: ID!, $updates: AdvancedPatternInput!) {
    updateDirectPattern(patternId: $patternId, updates: $updates) {
      id
      planId
      franchiseId
      sourceType
      managedBy
      resolvedDays
      resolvedScheduleType
      rotationData
      seasonalRules
      exceptionDates
      patternType
      startDate
      endDate
      nextGeneration
      lastGenerated
      isActive
      generateAhead
      createdAt
      updatedAt
      resolvedTimeIntervals {
        id
        startTime
        endTime
        sortOrder
        patternId
      }
      resolvedBreakRules {
        id
        afterHours
        breakType
        duration
        isPaid
        isRequired
        sortOrder
        patternId
      }
    }
  }
`);

const REGENERATE_PATTERN_FROM_TEMPLATE = gql(`
  mutation RegeneratePatternFromTemplate($planId: ID!) {
    regeneratePatternFromTemplate(planId: $planId)
  }
`);

const REGENERATE_PATTERN_FROM_CUSTOM_PLAN = gql(`
  mutation RegeneratePatternFromCustomPlan($planId: ID!) {
    regeneratePatternFromCustomPlan(planId: $planId)
  }
`);

const GENERATE_SCHEDULED_SHIFTS = gql(`
  mutation GenerateScheduledShifts {
    generateScheduledShifts
  }
`);

// =============================================
// SHIFT MUTATIONS (Layer 4)
// =============================================

const GENERATE_SHIFTS_FROM_PATTERN = gql(`
  mutation GenerateShiftsFromPattern($patternId: ID!) {
    generateShiftsFromPattern(patternId: $patternId) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const UPDATE_SHIFT = gql(`
  mutation UpdateShift($shiftId: ID!, $updates: UpdateShiftInput!) {
    updateShift(shiftId: $shiftId, updates: $updates) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const BULK_UPDATE_SHIFTS = gql(`
  mutation BulkUpdateShifts($shiftIds: [ID!]!, $updates: BulkShiftUpdates!, $reason: String!) {
    bulkUpdateShifts(shiftIds: $shiftIds, updates: $updates, reason: $reason) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const DELETE_SHIFT = gql(`
  mutation DeleteShift($shiftId: ID!, $reason: String!) {
    deleteShift(shiftId: $shiftId, reason: $reason)
  }
`);

const MARK_SHIFT_AS_EXCEPTION = gql(`
  mutation MarkShiftAsException($shiftId: ID!, $reason: String!) {
    markShiftAsException(shiftId: $shiftId, reason: $reason) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const ASSIGN_REPLACEMENT = gql(`
  mutation AssignReplacement($shiftId: ID!, $replacementWorkerSalonId: ID!) {
    assignReplacement(shiftId: $shiftId, replacementWorkerSalonId: $replacementWorkerSalonId) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

const REGENERATE_FUTURE_SHIFTS = gql(`
  mutation RegenerateFutureShifts($patternId: ID!) {
    regenerateFutureShifts(patternId: $patternId)
  }
`);

// =============================================
// TEMPLATE HOOKS (Layer 1)
// =============================================

export const useCreateShiftTemplate = () => {
  const [createMutation] = useMutation(CREATE_SHIFT_TEMPLATE);

  return useAsyncAction(
    async (franchiseId: string, input: CreateTemplateInput) => {
      const result = await createMutation({
        variables: { franchiseId, input },
        refetchQueries: ["GetShiftTemplates"],
      });
      return result.data?.createShiftTemplate;
    },
    {
      loadingMessage: "Creating shift template...",
      successMessage: "Shift template created successfully!",
      errorMessage: "Failed to create shift template",
    }
  );
};

export const useUpdateShiftTemplate = () => {
  const [updateMutation] = useMutation(UPDATE_SHIFT_TEMPLATE);

  return useAsyncAction(
    async (id: string, input: UpdateTemplateInput) => {
      const result = await updateMutation({
        variables: { id, input },
        refetchQueries: ["GetShiftTemplates", "GetShiftTemplate"],
      });
      return result.data?.updateShiftTemplate;
    },
    {
      loadingMessage: "Updating shift template...",
      successMessage: "Shift template updated successfully!",
      errorMessage: "Failed to update shift template",
    }
  );
};

export const useDeleteShiftTemplate = () => {
  const [deleteMutation] = useMutation(DELETE_SHIFT_TEMPLATE);

  return useAsyncAction(
    async (id: string) => {
      const result = await deleteMutation({
        variables: { id },
        refetchQueries: ["GetShiftTemplates"],
      });
      return result.data?.deleteShiftTemplate;
    },
    {
      loadingMessage: "Deleting shift template...",
      successMessage: "Shift template deleted successfully!",
      errorMessage: "Failed to delete shift template",
    }
  );
};

export const useDuplicateShiftTemplate = () => {
  const [duplicateMutation] = useMutation(DUPLICATE_SHIFT_TEMPLATE);

  return useAsyncAction(
    async (id: string, newName: string) => {
      const result = await duplicateMutation({
        variables: { id, newName },
        refetchQueries: ["GetShiftTemplates"],
      });
      return result.data?.duplicateShiftTemplate;
    },
    {
      loadingMessage: "Duplicating shift template...",
      successMessage: "Shift template duplicated successfully!",
      errorMessage: "Failed to duplicate shift template",
    }
  );
};

export const usePropagateTemplateChanges = () => {
  const [propagateMutation] = useMutation(PROPAGATE_TEMPLATE_CHANGES);

  return useAsyncAction(
    async (templateId: string) => {
      const result = await propagateMutation({
        variables: { templateId },
        refetchQueries: ["GetPlansForTemplate", "GetShiftPatterns"],
      });
      return result.data?.propagateTemplateChanges;
    },
    {
      loadingMessage: "Propagating template changes...",
      successMessage: "Template changes propagated successfully!",
      errorMessage: "Failed to propagate template changes",
    }
  );
};

// =============================================
// PLAN HOOKS (Layer 2)
// =============================================

export const useAssignTemplateToWorker = () => {
  const [assignMutation] = useMutation(ASSIGN_TEMPLATE_TO_WORKER);

  return useAsyncAction(
    async (templateId: string, workerSalonId: string) => {
      const result = await assignMutation({
        variables: { templateId, workerSalonId },
        refetchQueries: ["GetWorkerPlan", "GetPlansForTemplate"],
      });
      return result.data?.assignTemplateToWorker;
    },
    {
      loadingMessage: "Assigning template to worker...",
      successMessage: "Template assigned to worker successfully!",
      errorMessage: "Failed to assign template to worker",
    }
  );
};

export const useCreateCustomWorkerPlan = () => {
  const [createMutation] = useMutation(CREATE_CUSTOM_WORKER_PLAN);

  return useAsyncAction(
    async (workerSalonId: string, scheduleData: CustomScheduleInput) => {
      const result = await createMutation({
        variables: { workerSalonId, scheduleData },
        refetchQueries: ["GetWorkerPlan"],
      });
      return result.data?.createCustomWorkerPlan;
    },
    {
      loadingMessage: "Creating custom worker plan...",
      successMessage: "Custom worker plan created successfully!",
      errorMessage: "Failed to create custom worker plan",
    }
  );
};

export const useUpdateCustomWorkerPlan = () => {
  const [updateMutation] = useMutation(UPDATE_CUSTOM_WORKER_PLAN);

  return useAsyncAction(
    async (planId: string, scheduleData: CustomScheduleInput) => {
      const result = await updateMutation({
        variables: { planId, scheduleData },
        refetchQueries: ["GetWorkerPlan"],
      });
      return result.data?.updateCustomWorkerPlan;
    },
    {
      loadingMessage: "Updating custom worker plan...",
      successMessage: "Custom worker plan updated successfully!",
      errorMessage: "Failed to update custom worker plan",
    }
  );
};

export const useConvertTemplateToCustomPlan = () => {
  const [convertMutation] = useMutation(CONVERT_TEMPLATE_TO_CUSTOM_PLAN);

  return useAsyncAction(
    async (planId: string) => {
      const result = await convertMutation({
        variables: { planId },
        refetchQueries: ["GetWorkerPlan"],
      });
      return result.data?.convertTemplateToCustomPlan;
    },
    {
      loadingMessage: "Converting to custom plan...",
      successMessage: "Template converted to custom plan successfully!",
      errorMessage: "Failed to convert template to custom plan",
    }
  );
};

export const useDeleteWorkerPlan = () => {
  const [deleteMutation] = useMutation(DELETE_WORKER_PLAN);

  return useAsyncAction(
    async (planId: string) => {
      const result = await deleteMutation({
        variables: { planId },
        refetchQueries: ["GetWorkerPlan", "GetPlansForTemplate"],
      });
      return result.data?.deleteWorkerPlan;
    },
    {
      loadingMessage: "Deleting worker plan...",
      successMessage: "Worker plan deleted successfully!",
      errorMessage: "Failed to delete worker plan",
    }
  );
};

export const useReplaceWorkerPlan = () => {
  const [replaceMutation] = useMutation(REPLACE_WORKER_PLAN);

  return useAsyncAction(
    async (workerSalonId: string, templateId: string) => {
      const result = await replaceMutation({
        variables: { workerSalonId, templateId },
        refetchQueries: ["GetWorkerPlan", "GetPlansForTemplate", "GetShifts"],
      });
      return result.data?.replaceWorkerPlan;
    },
    {
      loadingMessage: "Assigning template with automatic pattern and shift generation...",
      successMessage: "Template assigned successfully with shifts generated!",
      errorMessage: "Failed to assign template",
    }
  );
};

// Atomic operation to switch template (replaces existing plan)
export const useSwitchWorkerTemplate = () => {
  const [replaceMutation] = useMutation(REPLACE_WORKER_PLAN);

  return useAsyncAction(
    async (_planId: string, templateId: string, workerSalonId: string) => {
      // Use atomic replace operation with automatic pattern/shift generation
      const result = await replaceMutation({
        variables: { templateId, workerSalonId },
        refetchQueries: ["GetWorkerPlan", "GetPlansForTemplate", "GetShifts"],
      });

      return result.data?.replaceWorkerPlan;
    },
    {
      loadingMessage: "Switching to new template...",
      successMessage: "Template switched successfully with patterns and shifts generated!",
      errorMessage: "Failed to switch template",
    }
  );
};

// =============================================
// PATTERN HOOKS (Layer 3)
// =============================================

export const useGeneratePatternFromTemplate = () => {
  const [generateMutation] = useMutation(GENERATE_PATTERN_FROM_TEMPLATE);

  return useAsyncAction(
    async (planId: string, templateId: string) => {
      const result = await generateMutation({
        variables: { planId, templateId },
        refetchQueries: ["GetShiftPattern"],
      });
      return result.data?.generatePatternFromTemplate;
    },
    {
      loadingMessage: "Generating pattern from template...",
      successMessage: "Pattern generated successfully!",
      errorMessage: "Failed to generate pattern from template",
    }
  );
};

export const useGeneratePatternFromCustomPlan = () => {
  const [generateMutation] = useMutation(GENERATE_PATTERN_FROM_CUSTOM_PLAN);

  return useAsyncAction(
    async (planId: string) => {
      const result = await generateMutation({
        variables: { planId },
        refetchQueries: ["GetShiftPattern"],
      });
      return result.data?.generatePatternFromCustomPlan;
    },
    {
      loadingMessage: "Generating pattern from custom plan...",
      successMessage: "Pattern generated successfully!",
      errorMessage: "Failed to generate pattern from custom plan",
    }
  );
};

export const useCreateDirectPattern = () => {
  const [createMutation] = useMutation(CREATE_DIRECT_PATTERN);

  return useAsyncAction(
    async (franchiseId: string, workerSalonId: string, patternData: AdvancedPatternInput) => {
      const result = await createMutation({
        variables: { franchiseId, workerSalonId, patternData },
        refetchQueries: ["GetShiftPattern"],
      });
      return result.data?.createDirectPattern;
    },
    {
      loadingMessage: "Creating direct pattern...",
      successMessage: "Direct pattern created successfully!",
      errorMessage: "Failed to create direct pattern",
    }
  );
};

export const useUpdateDirectPattern = () => {
  const [updateMutation] = useMutation(UPDATE_DIRECT_PATTERN);

  return useAsyncAction(
    async (patternId: string, updates: AdvancedPatternInput) => {
      const result = await updateMutation({
        variables: { patternId, updates },
        refetchQueries: ["GetShiftPattern"],
      });
      return result.data?.updateDirectPattern;
    },
    {
      loadingMessage: "Updating direct pattern...",
      successMessage: "Direct pattern updated successfully!",
      errorMessage: "Failed to update direct pattern",
    }
  );
};

export const useRegeneratePatternFromTemplate = () => {
  const [regenerateMutation] = useMutation(REGENERATE_PATTERN_FROM_TEMPLATE);

  return useAsyncAction(
    async (planId: string) => {
      const result = await regenerateMutation({
        variables: { planId },
        refetchQueries: ["GetShiftPattern", "GetShifts"],
      });
      return result.data?.regeneratePatternFromTemplate;
    },
    {
      loadingMessage: "Regenerating pattern from template...",
      successMessage: "Pattern regenerated successfully!",
      errorMessage: "Failed to regenerate pattern from template",
    }
  );
};

export const useRegeneratePatternFromCustomPlan = () => {
  const [regenerateMutation] = useMutation(REGENERATE_PATTERN_FROM_CUSTOM_PLAN);

  return useAsyncAction(
    async (planId: string) => {
      const result = await regenerateMutation({
        variables: { planId },
        refetchQueries: ["GetShiftPattern", "GetShifts"],
      });
      return result.data?.regeneratePatternFromCustomPlan;
    },
    {
      loadingMessage: "Regenerating pattern from custom plan...",
      successMessage: "Pattern regenerated successfully!",
      errorMessage: "Failed to regenerate pattern from custom plan",
    }
  );
};

export const useGenerateScheduledShifts = () => {
  const [generateMutation] = useMutation(GENERATE_SCHEDULED_SHIFTS);

  return useAsyncAction(
    async () => {
      const result = await generateMutation({
        refetchQueries: ["GetShifts"],
      });
      return result.data?.generateScheduledShifts;
    },
    {
      loadingMessage: "Generating scheduled shifts...",
      successMessage: "Scheduled shifts generated successfully!",
      errorMessage: "Failed to generate scheduled shifts",
    }
  );
};

// =============================================
// SHIFT HOOKS (Layer 4)
// =============================================

export const useGenerateShiftsFromPattern = () => {
  const [generateMutation] = useMutation(GENERATE_SHIFTS_FROM_PATTERN);

  return useAsyncAction(
    async (patternId: string) => {
      const result = await generateMutation({
        variables: { patternId },
        refetchQueries: ["GetShifts"],
      });
      return result.data?.generateShiftsFromPattern;
    },
    {
      loadingMessage: "Generating shifts from pattern...",
      successMessage: "Shifts generated successfully!",
      errorMessage: "Failed to generate shifts from pattern",
    }
  );
};

export const useUpdateShift = () => {
  const [updateMutation] = useMutation(UPDATE_SHIFT);

  return useAsyncAction(
    async (shiftId: string, updates: UpdateShiftInput) => {
      const result = await updateMutation({
        variables: { shiftId, updates },
        refetchQueries: ["GetShifts", "GetShiftHistory"],
      });
      return result.data?.updateShift;
    },
    {
      loadingMessage: "Updating shift...",
      successMessage: "Shift updated successfully!",
      errorMessage: "Failed to update shift",
    }
  );
};

export const useBulkUpdateShifts = () => {
  const [bulkUpdateMutation] = useMutation(BULK_UPDATE_SHIFTS);

  return useAsyncAction(
    async (shiftIds: string[], updates: BulkShiftUpdates, reason: string) => {
      const result = await bulkUpdateMutation({
        variables: { shiftIds, updates, reason },
        refetchQueries: ["GetShifts"],
      });
      return result.data?.bulkUpdateShifts;
    },
    {
      loadingMessage: "Updating shifts...",
      successMessage: "Shifts updated successfully!",
      errorMessage: "Failed to update shifts",
    }
  );
};

export const useDeleteShift = () => {
  const [deleteMutation] = useMutation(DELETE_SHIFT);

  return useAsyncAction(
    async (shiftId: string, reason: string) => {
      const result = await deleteMutation({
        variables: { shiftId, reason },
        refetchQueries: ["GetShifts"],
      });
      return result.data?.deleteShift;
    },
    {
      loadingMessage: "Deleting shift...",
      successMessage: "Shift deleted successfully!",
      errorMessage: "Failed to delete shift",
    }
  );
};

export const useMarkShiftAsException = () => {
  const [markMutation] = useMutation(MARK_SHIFT_AS_EXCEPTION);

  return useAsyncAction(
    async (shiftId: string, reason: string) => {
      const result = await markMutation({
        variables: { shiftId, reason },
        refetchQueries: ["GetShifts", "GetShiftHistory"],
      });
      return result.data?.markShiftAsException;
    },
    {
      loadingMessage: "Marking shift as exception...",
      successMessage: "Shift marked as exception successfully!",
      errorMessage: "Failed to mark shift as exception",
    }
  );
};

export const useAssignReplacement = () => {
  const [assignMutation] = useMutation(ASSIGN_REPLACEMENT);

  return useAsyncAction(
    async (shiftId: string, replacementWorkerSalonId: string) => {
      const result = await assignMutation({
        variables: { shiftId, replacementWorkerSalonId },
        refetchQueries: ["GetShifts", "GetShiftHistory"],
      });
      return result.data?.assignReplacement;
    },
    {
      loadingMessage: "Assigning replacement worker...",
      successMessage: "Replacement worker assigned successfully!",
      errorMessage: "Failed to assign replacement worker",
    }
  );
};

export const useRegenerateFutureShifts = () => {
  const [regenerateMutation] = useMutation(REGENERATE_FUTURE_SHIFTS);

  return useAsyncAction(
    async (patternId: string) => {
      const result = await regenerateMutation({
        variables: { patternId },
        refetchQueries: ["GetShifts"],
      });
      return result.data?.regenerateFutureShifts;
    },
    {
      loadingMessage: "Regenerating future shifts...",
      successMessage: "Future shifts regenerated successfully!",
      errorMessage: "Failed to regenerate future shifts",
    }
  );
};

// =============================================
// LEGACY COMPATIBILITY HOOKS
// =============================================

// Legacy hook aliases for backward compatibility
export const useCreateRecurringShifts = useGenerateScheduledShifts;
export const useCreateShift = useGenerateShiftsFromPattern;
export const usePublishShifts = useBulkUpdateShifts; // Temporary mapping
export const useBulkPublishShifts = useBulkUpdateShifts; // Temporary mapping
